import React, { useState } from "react";
import { styled } from "@mui/system";
import {
  VulnerabilityDetailsContainer,
  VulnerabilityText,
  VulnerabilityTitle,
  EmailSection,
  EmailInputWrapper,
  EmailInput,
  EmailButton,
} from "./ProductDetailsForm.js";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";

const ThanksMessage = styled("div")({
  color: "#10b981",
  fontSize: "18px",
  fontWeight: "600",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  textAlign: "center",
  marginTop: "15px",
 marginBottom:"20px"
});

const NoCVEMessage = styled("div")({
  padding: "20px",
  textAlign: "center",
  color: "#10b981",
  fontSize: "16px",
  fontWeight: "500",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  background: "rgba(16, 185, 129, 0.1)",
  borderRadius: "8px",
  border: "1px solid rgba(16, 185, 129, 0.3)",
  marginBottom: "20px",
});

const CVETable = styled("table")({
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: "0 8px",
  marginBottom: "20px",
});

const CVETableHeader = styled("th")({
  padding: "12px 15px",
  textAlign: "left",
  fontSize: "14px",
  fontWeight: "600",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#94a3b8",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
});

const CVETableRow = styled("tr")({
  background: "rgba(255, 255, 255, 0.05)",
  transition: "all 0.3s ease",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.08)",
  },
});

const CVETableCell = styled("td")({
  padding: "15px",
  fontSize: "15px",
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#e2e8f0",
  borderTop: "1px solid rgba(255, 255, 255, 0.05)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.05)",

  "&:first-of-type": {
    borderLeft: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "8px 0 0 8px",
    color: "#22d3ee",
    fontWeight: "600",
  },

  "&:last-of-type": {
    borderRight: "1px solid rgba(255, 255, 255, 0.05)",
    borderRadius: "0 8px 8px 0",
  },
});

const VulnerabilityDetails = ({ riskScore, vendorData, productData }) => {
  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();

  const handleSendEmail = async () => {
    if (email) {
      setEmailSent(true);
      // Here you can add actual email sending logic
      console.log("Sending vulnerabilities list to:", email, vendorData, productData);
      // navigate(`/reports?email=${email}&brand=${vendorData}&product=${productData}`)
      console.log("process.env.REACT_APP_EMAILJS_SERVICE_ID", process.env.REACT_APP_EMAILJS_SERVICE_ID,process.env.REACT_APP_EMAILJS_SERVICE_ID, process.env.REACT_APP_EMAILJS_PUBLIC_KEY )
     const sendEmail =  await  emailjs.send(process.env.REACT_APP_EMAILJS_SERVICE_ID, process.env.REACT_APP_EMAILJS_TEMPLATE_ID,  {
        user_email: email,
        vendor: encodeURIComponent(vendorData),
        product: encodeURIComponent(productData)
      }, process.env.REACT_APP_EMAILJS_PUBLIC_KEY).then(
          function (response) {
            console.log('SUCCESS!', response);
          },
          function (err) {
            console.log('FAILED...', err);
          },
        );

    }
  };

  if (!riskScore) return null;

  return (
    <VulnerabilityDetailsContainer>
      <VulnerabilityText>
        This device has {riskScore.totalCVEs} known vulnerabilit{riskScore.totalCVEs !== 1 ? 'ies' : 'y'} and is considered at {riskScore.riskTier.text.toLowerCase()} of being exploited by hackers according to the industry-standard EPSS scoring system. EPSS is an acronym for the Exploit Prediction Scoring System, driven by the non-profit Forum of Incident Response and Security Teams (FIRST).
      </VulnerabilityText>

      {riskScore.totalCVEs > 0 ? (
        <>
          <VulnerabilityTitle>
            The top {Math.min(3, riskScore.totalCVEs)} critical vulnerabilit{Math.min(3, riskScore.totalCVEs) > 1 ? 'ies' : 'y'} on this device:
          </VulnerabilityTitle>

          <CVETable>
            <thead>
              <tr>
                <CVETableHeader>CVE ID</CVETableHeader>
                <CVETableHeader>EPSS Score</CVETableHeader>
              </tr>
            </thead>
            <tbody>
              {(riskScore.cveDetails || []).slice(0, 3).map((cveDetail, index) => {
                if (!cveDetail) return null;

                // Handle both nested and flat data structures
                const cveId = cveDetail.cveId || cveDetail.data?.cveId || `CVE ${index + 1}`;
                const epssScore = cveDetail.epssScore || cveDetail.epss || cveDetail.data?.epss || '0.0000';

                return (
                  <CVETableRow key={index}>
                    <CVETableCell>{cveId}</CVETableCell>
                    <CVETableCell>{epssScore}</CVETableCell>
                  </CVETableRow>
                );
              })}
            </tbody>
          </CVETable>

          <EmailSection>
            {emailSent ? (
              <ThanksMessage>Thanks – your list is on the way.</ThanksMessage>
            ) : (
              <>
                <VulnerabilityText style={{ marginBottom: '10px', textAlign: 'center' }}>
                  Enter your email address to get the full list of vulnerabilities on this device.
                </VulnerabilityText>

                <EmailInputWrapper>
                  <EmailInput
                    type="email"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <EmailButton onClick={handleSendEmail}>
                    Send me the full list of vulnerabilities
                  </EmailButton>
                </EmailInputWrapper>
              </>
            )}
          </EmailSection>
        </>
      ) : (
        <NoCVEMessage>
           No vulnerabilities found for this device. It appears to be secure.
        </NoCVEMessage>
      )}
    </VulnerabilityDetailsContainer>
  );
};

export default VulnerabilityDetails;
